import React, { useState } from 'react';
import { toast } from '../utils/toast';
import { geminiService } from '../services/geminiService';
import { QuickStart } from '../components/QuickStart';
import { VoiceRecorder } from '../components/VoiceRecorder';
import { BetaBadge, BetaMessage } from '../components/BetaDisclaimer';
import DeploymentOptions from '../components/DeploymentOptions';

export function PySparkWriter() {
  const [transformationInput, setTransformationInput] = useState('');
  const [operationType, setOperationType] = useState('transform');
  const [optimizationFocus, setOptimizationFocus] = useState('performance');
  const [sparkVersion, setSparkVersion] = useState('3.4');
  const [platform, setPlatform] = useState('databricks');
  const [generatedCode, setGeneratedCode] = useState('');
  const [codeExplanation, setCodeExplanation] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showDestructiveWarning, setShowDestructiveWarning] = useState(false);

  const operationOptions = [
    { value: 'transform', label: 'Transform', description: 'Data transformation operations' },
    { value: 'read', label: 'Read', description: 'Load and read data' },
    { value: 'create', label: 'Create', description: 'Create new datasets', isDestructive: true },
    { value: 'update', label: 'Update', description: 'Modify existing data', isDestructive: true },
    { value: 'delete', label: 'Delete', description: 'Remove data', isDestructive: true },
    { value: 'aggregate', label: 'Aggregate', description: 'Group and aggregate data' },
    { value: 'join', label: 'Join', description: 'Combine multiple datasets' },
    { value: 'window', label: 'Window', description: 'Window functions and analytics' },
    { value: 'ml', label: 'ML Pipeline', description: 'Machine learning operations' },
    { value: 'streaming', label: 'Streaming', description: 'Real-time data processing' }
  ];

  const optimizationOptions = [
    { value: 'performance', label: 'Performance Optimized' },
    { value: 'memory', label: 'Memory Efficient' },
    { value: 'readability', label: 'Readable Code' },
    { value: 'distributed', label: 'Distributed Processing' }
  ];

  const sparkVersionOptions = [
    { value: '3.5', label: 'Spark 3.5' },
    { value: '3.4', label: 'Spark 3.4' },
    { value: '3.3', label: 'Spark 3.3' },
    { value: '3.2', label: 'Spark 3.2' }
  ];

  const platformOptions = [
    { value: 'databricks', label: 'Databricks', description: 'Databricks Lakehouse Platform' },
    { value: 'emr', label: 'AWS EMR', description: 'Amazon EMR Spark' },
    { value: 'dataproc', label: 'Dataproc', description: 'Google Cloud Dataproc' },
    { value: 'hdinsight', label: 'HDInsight', description: 'Azure HDInsight' },
    { value: 'standalone', label: 'Standalone', description: 'Apache Spark Standalone' }
  ];

  const quickStartGuides = [
    {
      title: "Data Pipeline Design",
      description: "Plan your PySpark transformations for optimal data processing.",
      keywords: ["Schema", "Transformations", "Pipeline"],
      example: "Example: Transform and clean customer transaction data, handling missing values and duplicates",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      )
    },
    {
      title: "Performance Optimization",
      description: "Optimize your PySpark code for better performance.",
      keywords: ["Performance", "Scalability", "Efficiency"],
      example: "Example: Optimize a complex join operation with proper partitioning strategy",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      title: "Production Deployment",
      description: "Prepare your code for production deployment.",
      keywords: ["Testing", "Deployment", "Monitoring"],
      example: "Example: Set up error handling and logging for a production data pipeline",
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      )
    }
  ];

  const handleTranscriptionComplete = (transcribedText) => {
    setTransformationInput(transcribedText);
  };

  const isDestructiveOperation = (type) => {
    return operationOptions.find(opt => opt.value === type)?.isDestructive || false;
  };

  const parseGeneratedResponse = (response) => {
    // Split response into code and explanation sections
    const sections = response.split(/(?=Code Explanation:|Performance Considerations:|Best Practices:|Implementation Notes:|Safety Considerations:)/i);
    
    // First section should be the PySpark code
    const code = sections[0].trim();
    
    // Combine remaining sections into explanation
    const explanation = sections.slice(1).join('\n\n').trim();
    
    return { code, explanation };
  };

  const generateCode = async () => {
    if (!transformationInput.trim()) {
      toast.error('Please provide transformation requirements');
      return;
    }

    if (isDestructiveOperation(operationType)) {
      setShowDestructiveWarning(true);
      return;
    }

    setIsGenerating(true);
    try {
      const prompt = `Generate optimized PySpark code for: ${transformationInput}
Operation Type: ${operationType}
Optimization Focus: ${optimizationFocus}
Spark Version: ${sparkVersion}
Platform: ${platform}

Please provide:
- Complete PySpark code with imports (start with "PySpark Code:" on a new line)
- Code explanation and structure (start with "Code Explanation:" on a new line)
- Performance considerations (start with "Performance Considerations:" on a new line)
- Best practices applied (start with "Best Practices:" on a new line)
- Implementation notes (start with "Implementation Notes:" on a new line)
- Safety considerations (start with "Safety Considerations:" on a new line)

Format the response with clear sections and include detailed comments in the code.`;

      const responseText = await geminiService.generateCode(prompt, {
        language: 'python',
        framework: 'pyspark'
      });

      // Check if there's an error message in the response
      if (responseText && typeof responseText === 'string') {
        if (responseText.includes('Error generating code:')) {
          throw new Error(responseText);
        }
        
        const { code, explanation } = parseGeneratedResponse(responseText);
        setGeneratedCode(code);
        setCodeExplanation(explanation);
        toast.success('Code generated successfully!');
      } else {
        throw new Error('No content received from the API');
      }
    } catch (error) {
      console.error('Error generating code:', error);
      toast.error(error.message || 'Failed to generate code');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard!');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Modern SaaS Header */}
      <div className="relative mb-8 p-8 rounded-3xl bg-gradient-to-r from-yellow-900 via-yellow-800 to-yellow-900 shadow-2xl overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          <div className="absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent"></div>
          <div className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent"></div>
          
          <div className="absolute -left-48 -top-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-yellow-500/40 via-yellow-300/10 to-yellow-400/40 blur-xl opacity-30"></div>
          </div>
          <div className="absolute -right-48 -bottom-48 h-96 w-96">
            <div className="absolute inset-0 rotate-45 translate-x-1/2 translate-y-1/2 bg-gradient-conic from-yellow-400/40 via-yellow-300/10 to-yellow-500/40 blur-xl opacity-30"></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-4">
              <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-yellow-800/50 border border-yellow-700/50 backdrop-blur-sm">
                <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse"></div>
                <span className="text-sm text-yellow-100">AI-Powered PySpark</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white flex items-center">
                PySpark Writer
                <BetaBadge className="ml-3" />
              </h1>
              <p className="text-lg text-yellow-200 max-w-xl">
                Create efficient PySpark transformations and analytics code
              </p>
              <BetaMessage />
              <div className="flex items-center gap-4 pt-2">
                <div className="flex items-center space-x-2 text-sm text-yellow-200">
                  <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Multiple Operations</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-yellow-200">
                  <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Performance Optimized</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .bg-gradient-conic {
          background-image: conic-gradient(var(--tw-gradient-stops));
        }
      `}</style>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="p-6">
          {/* Target Audience Banner */}
          <div className="mb-8 p-4 bg-yellow-50 rounded-lg">
            <h3 className="text-lg font-medium text-yellow-900 mb-2">Who is this tool for?</h3>
            <ul className="list-disc list-inside text-yellow-700 space-y-1">
              <li>Data Engineers - Design and optimize data pipelines</li>
              <li>Data Scientists - Implement ML pipelines and analytics</li>
              <li>Analytics Engineers - Build efficient data transformations</li>
            </ul>
          </div>

          <div className="space-y-8">
            {/* Input Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transformation Requirements
              </label>
              <div className="mb-2">
                <div className="text-sm text-gray-500">Example: "Transform customer transaction data by cleaning missing values, removing duplicates, and calculating daily aggregates by customer segment"</div>
              </div>
              <textarea
                value={transformationInput}
                onChange={(e) => setTransformationInput(e.target.value)}
                rows="4"
                placeholder="Describe your PySpark transformation requirements..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>

            {/* Operation Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Operation Type
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {operationOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setOperationType(option.value)}
                    className={`flex items-center justify-center p-3 rounded-lg border ${
                      operationType === option.value
                        ? 'bg-yellow-50 border-yellow-500 text-yellow-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Platform Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Platform
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {platformOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setPlatform(option.value)}
                    className={`flex items-center justify-center p-3 rounded-lg border ${
                      platform === option.value
                        ? 'bg-yellow-50 border-yellow-500 text-yellow-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Optimization Focus */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Optimization Focus
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {optimizationOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setOptimizationFocus(option.value)}
                    className={`flex items-center justify-center p-3 rounded-lg border ${
                      optimizationFocus === option.value
                        ? 'bg-yellow-50 border-yellow-500 text-yellow-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Spark Version */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Spark Version
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {sparkVersionOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setSparkVersion(option.value)}
                    className={`flex items-center justify-center p-3 rounded-lg border ${
                      sparkVersion === option.value
                        ? 'bg-yellow-50 border-yellow-500 text-yellow-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={generateCode}
              disabled={isGenerating}
              className={`w-full px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 ${
                isGenerating
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-yellow-600 hover:bg-yellow-700'
              }`}
            >
              {isGenerating ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Generating Code...
                </div>
              ) : (
                'Generate Code'
              )}
            </button>

            {/* Destructive Operation Warning Modal */}
            {showDestructiveWarning && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                <div className="bg-white rounded-xl p-6 max-w-md">
                  <div className="flex items-center text-red-600 mb-4">
                    <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <h3 className="text-lg font-medium">Warning: Data Modification Operation</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    You are about to generate code that could modify or delete data. Please ensure you:
                    <ul className="list-disc list-inside mt-2">
                      <li>Have proper backups of your data</li>
                      <li>Understand the impact on your data lake/warehouse</li>
                      <li>Have necessary access permissions</li>
                      <li>Test the code in a development environment first</li>
                    </ul>
                  </p>
                  <div className="flex justify-end space-x-4">
                    <button
                      onClick={() => setShowDestructiveWarning(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        setShowDestructiveWarning(false);
                        generateCode();
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Proceed Anyway
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Generated Code Section */}
            {generatedCode ? (
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Generated PySpark Code</h3>
                  <button
                    onClick={() => copyToClipboard(generatedCode)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* PySpark Code */}
                  <div className="prose max-w-none">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">PySpark Code</h4>
                    <pre className="bg-gray-800 text-gray-100 rounded-lg p-4 overflow-x-auto">
                      <code>{generatedCode}</code>
                    </pre>
                  </div>
                  
                  {/* Code Explanation */}
                  <div className="prose max-w-none">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Code Explanation</h4>
                    <div className="bg-white rounded-lg p-4 border border-gray-200 overflow-y-auto max-h-[500px]">
                      <div className="markdown-content">
                        {codeExplanation.split('\n').map((line, index) => {
                          // Check if line is a section header
                          if (line.match(/^(Code Explanation|Performance Considerations|Best Practices|Implementation Notes|Safety Considerations):/i)) {
                            return <h5 key={index} className="font-medium text-gray-900 mt-4 mb-2">{line}</h5>;
                          }
                          return <p key={index} className="text-gray-600 mb-2">{line}</p>;
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No code generated yet</h3>
                <p className="mt-1 text-sm text-gray-500">Start by describing your transformation requirements.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <QuickStart guides={quickStartGuides} />
      <DeploymentOptions appId="pyspark-writer" position="none" />
    </div>
  );
}

export default PySparkWriter; 